import React, { useState, useEffect } from 'react';
import { Building, TrendingUp } from 'lucide-react';
import axios from 'axios';

// Asegúrate de que esta URL coincida con tu backend
const API_URL = 'https://backend-production-1786.up.railway.app';

const FeaturedLocations = ({ onSelectLocation }) => {
  const [topCities, setTopCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useFallback, setUseFallback] = useState(false);

  // Datos de respaldo por si falla la API
  const FALLBACK_CITIES = [
    { id: 1, ciudad: "Lima", pais: "Perú", n_propiedades: 1245, latitude: -12.0464, longitude: -77.0428 },
    { id: 2, ciudad: "Arequipa", pais: "Perú", n_propiedades: 534, latitude: -16.4090, longitude: -71.5375 },
    { id: 3, ciudad: "Trujillo", pais: "Perú", n_propiedades: 423, latitude: -8.1116, longitude: -79.0289 },
    { id: 4, ciudad: "Chiclayo", pais: "Perú", n_propiedades: 312, latitude: -6.7763, longitude: -79.8437 },
    { id: 5, ciudad: "Cusco", pais: "Perú", n_propiedades: 298, latitude: -13.5319, longitude: -71.9675 }
  ];

  useEffect(() => {
    const fetchTopCities = async () => {
      try {
        setLoading(true);
        
        // Si estamos en modo fallback, usar datos estáticos
        if (useFallback) {
          setTopCities(FALLBACK_CITIES);
          setLoading(false);
          return;
        }
        
        console.log("Fetching top cities from:", `${API_URL}/api/ciudades/top?limit=5`);
        
        const response = await axios.get(`${API_URL}/api/ciudades/top?limit=5`, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          timeout: 5000
        });
        
        console.log("Top cities response:", response.data);
        
        if (response.data && response.data.cities) {
          setTopCities(response.data.cities);
          setError(null);
        } else {
          console.log("Estructura inesperada en respuesta:", response.data);
          throw new Error("Formato de respuesta no esperado");
        }
      } catch (error) {
        console.error('Error fetching top cities:', error);
        setError("Error al cargar ciudades destacadas");
        
        // Activar el modo fallback con datos estáticos
        setUseFallback(true);
        setTopCities(FALLBACK_CITIES);
      } finally {
        setLoading(false);
      }
    };

    fetchTopCities();
  }, [useFallback]);

  // Estado de carga
  if (loading) {
    return (
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-2 mb-3 text-gray-700">
          <TrendingUp className="w-4 h-4" />
          <span className="text-sm font-medium">Ciudades destacadas</span>
        </div>
        <div className="animate-pulse flex flex-wrap gap-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-8 w-28 bg-gray-200 rounded-full"></div>
          ))}
        </div>
      </div>
    );
  }

  // Estado de error pero con datos fallback
  if (error && !useFallback) {
    return (
      <div className="p-4 border-b border-gray-100">
        <div className="text-red-500 text-sm">{error}</div>
        <button 
          className="mt-2 text-sm text-indigo-600 underline"
          onClick={() => setUseFallback(false)}
        >
          Reintentar
        </button>
      </div>
    );
  }

  // Sin ciudades
  if (topCities.length === 0) {
    return null;
  }

  // Renderizado normal
  return (
    <div className="p-4 border-b border-gray-100">
      <div className="flex items-center gap-2 mb-3 text-gray-700">
        <TrendingUp className="w-4 h-4" />
        <span className="text-sm font-medium">Ciudades destacadas</span>
        {useFallback && (
          <span className="text-xs text-amber-600">(datos de ejemplo)</span>
        )}
      </div>
      
      <div className="flex flex-wrap gap-2">
        {topCities.map(city => (
          <button
            key={city.id}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 
                     text-indigo-700 rounded-full text-sm transition-colors"
            onClick={() => onSelectLocation({
              id: city.id,
              display: city.ciudad,
              secondaryText: city.pais,
              type: 'city',
              value: city.ciudad,
              extraData: {
                countryCode: city.pais,
                propertyCount: city.n_propiedades,
                latitude: city.latitude,
                longitude: city.longitude
              }
            })}
          >
            <Building className="w-3.5 h-3.5" />
            <span>{city.ciudad}</span>
            {city.n_propiedades && (
              <span className="text-xs text-indigo-500 bg-indigo-100 rounded-full px-1.5">
                {city.n_propiedades}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default FeaturedLocations;